import { ComponentType, ReactNode, useState } from 'react'
import { Box, Card, CardContent, CardHeader, MenuItem, Skeleton, Stack, TextField, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import siteMap from '../../../siteMap'
import {
  CanonicalPeriod,
  EmissionTier,
  getPeriodYear,
  MetricState,
  metricStateLabels,
  PERIODS,
} from '../../climateAnalytics/common'
import { usePropertyClimateData, type PropertyResult } from '../../../hooks/climateData'
import { FloodScoreBadge } from '../../climateAnalytics/common/flood/utils'
import { ExtremeWindScoreBadge } from '../../climateAnalytics/common/extremeWind/utils'
import { HeatStressScoreBadge } from '../../climateAnalytics/common/heatStress/utils'
import { WildfireScoreBadge } from '../../climateAnalytics/common/fireRisk/utils'
import { parseWaterStressScore, WaterRiskScoreBadge } from '../../climateAnalytics/common/waterRisk/utils'
import { getClimateHazardViewConfig } from '../../climateAnalytics/common/hazardConfig'
import ClimateHazardIcon from '../../climateAnalytics/common/ClimateHazardIcon'
import ClimateControlField from '../../climateAnalytics/common/ClimateControlField'

type ScoreBadgeComponent = ComponentType<{ score: number | null; size?: any }>

type HazardConfig = {
  key: string
  hazardCode: string
  label: string
  badge: ScoreBadgeComponent
  getScore: (result: PropertyResult | undefined, tier: EmissionTier, period: CanonicalPeriod) => number | null
}

type CompactControlOption<T extends string> = {
  value: T
  label: string
}

type HazardLabelLinkProps = {
  children: ReactNode
  label: string
  to: string | null
}

function parseNumericValue(node: any): number | null {
  if (node == null) return null
  if (typeof node === 'number') return Number.isNaN(node) ? null : node
  if (typeof node === 'string') {
    const parsed = Number.parseFloat(node)
    return Number.isNaN(parsed) ? null : parsed
  }
  if (Array.isArray(node)) {
    if (!node.length) return null
    return parseNumericValue(node[0]?.y ?? node[0])
  }
  if (typeof node === 'object') {
    if (typeof (node as any).y !== 'undefined') return parseNumericValue((node as any).y)
    if (typeof (node as any).value !== 'undefined') return parseNumericValue((node as any).value)
  }
  return null
}

function extractPeriodScore(
  result: PropertyResult | undefined,
  emissionTier: EmissionTier,
  period: CanonicalPeriod,
): number | null {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) return null
  const tierData = result.data?.data?.[emissionTier]
  if (!tierData) return null
  const point = (tierData as any)[period] ?? tierData
  return parseNumericValue(point)
}

function extractScalarScore(result: PropertyResult | undefined, emissionTier: EmissionTier): number | null {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) return null
  const tierData = result.data?.data?.[emissionTier]
  return parseNumericValue(tierData)
}

const emissionControls = [
  { value: EmissionTier.LOW, label: 'Low' },
  { value: EmissionTier.MODERATE, label: 'Medium' },
  { value: EmissionTier.HIGH, label: 'High' },
]

const horizonControls = PERIODS.map(period => ({ value: period, label: String(getPeriodYear(period)) }))

const hazards: HazardConfig[] = [
  {
    key: 'flood',
    hazardCode: 'fld',
    label: 'Flood',
    badge: FloodScoreBadge,
    getScore: extractPeriodScore,
  },
  {
    key: 'wildfire',
    hazardCode: 'wf',
    label: 'Wildfire',
    badge: WildfireScoreBadge,
    getScore: extractPeriodScore,
  },
  {
    key: 'heat',
    hazardCode: 'hs',
    label: 'Heat Stress',
    badge: HeatStressScoreBadge,
    getScore: extractPeriodScore,
  },
  {
    key: 'water',
    hazardCode: 'wr',
    label: 'Water Stress',
    badge: WaterRiskScoreBadge,
    getScore: parseWaterStressScore,
  },
  {
    key: 'wind',
    hazardCode: 'tc',
    label: 'Extreme Wind',
    badge: ExtremeWindScoreBadge,
    getScore: (result, tier) => extractScalarScore(result, tier),
  },
]
// TODO: Sort hazards by highest to lowest score, it's fine to sort it by raw scores.

function HazardLabelLink({ children, label, to }: HazardLabelLinkProps) {
  return (
    <Box
      component={to ? RouterLink : 'span'}
      to={to ?? undefined}
      aria-label={to ? `Open ${label} analytics` : undefined}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        p: 0.25,
        ml: -0.25,
        borderRadius: 1,
        color: 'text.primary',
        textDecoration: 'none',
        transition: theme =>
          theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration.shortest,
          }),
        ...(to
          ? {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
                color: 'primary.main',
              },
              '&:hover .hazard-label': {
                textDecoration: 'underline',
              },
              '&:focus-visible': {
                outline: theme => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }
          : {}),
      }}
    >
      {children}
    </Box>
  )
}

function CompactSelectControl<T extends string>({
  label,
  helpText,
  value,
  options,
  onChange,
  ariaLabel,
}: {
  label: string
  helpText: string
  value: T
  options: CompactControlOption<T>[]
  onChange: (value: T) => void
  ariaLabel: string
}) {
  return (
    <Box sx={{ minWidth: 104 }}>
      <ClimateControlField label={label} helpText={helpText}>
        <TextField
          select
          value={value}
          onChange={event => onChange(event.target.value as T)}
          inputProps={{ 'aria-label': ariaLabel }}
          size="small"
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              height: 32,
              borderRadius: 1.5,
              fontSize: 12,
              fontWeight: 700,
              backgroundColor: 'background.paper',
            },
            '& .MuiSelect-select': {
              py: 0.5,
              pl: 1,
            },
          }}
        >
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </ClimateControlField>
    </Box>
  )
}

const PropertyClimateHazardsCard = ({ propertyId }: { propertyId?: number | string }) => {
  const propertyIdNumber = typeof propertyId === 'number' ? propertyId : Number(propertyId)

  const [emissionTier, setEmissionTier] = useState<EmissionTier>(EmissionTier.HIGH)
  const [period, setPeriod] = useState<CanonicalPeriod>(CanonicalPeriod.LONG_TERM)

  const floodQuery = usePropertyClimateData('fld:score', { propertyId: propertyIdNumber })
  const wildfireQuery = usePropertyClimateData('wf:score', { propertyId: propertyIdNumber })
  const heatQuery = usePropertyClimateData('hs:heat_scores', { propertyId: propertyIdNumber })
  const waterQuery = usePropertyClimateData('wr:ws', { propertyId: propertyIdNumber })
  const windQuery = usePropertyClimateData('tc:score', { propertyId: propertyIdNumber })

  const hazardQueries: Record<string, ReturnType<typeof usePropertyClimateData>> = {
    flood: floodQuery,
    wildfire: wildfireQuery,
    heat: heatQuery,
    water: waterQuery,
    wind: windQuery,
  }

  const hazardRows = hazards.map(hazard => {
    const query = hazardQueries[hazard.key]
    const hazardViewConfig = getClimateHazardViewConfig(hazard.hazardCode)
    const hasSelectedPeriod = hazardViewConfig.availablePeriods.includes(period)
    const targetPeriod = hasSelectedPeriod ? period : hazardViewConfig.defaultPeriod
    const analyticsPath = Number.isFinite(propertyIdNumber)
      ? siteMap.PropertyClimateAnalytics.getPath(propertyIdNumber, hazard.hazardCode, emissionTier, targetPeriod)
      : null

    return {
      ...hazard,
      query,
      analyticsPath,
      score: hasSelectedPeriod ? hazard.getScore(query?.data, emissionTier, period) : null,
    }
  })

  const allLoading = hazardRows.every(hazard => hazard.query.isLoading && !hazard.query.data)

  return (
    <Card>
      <CardHeader
        title="Climate Risk Overview"
        action={
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            }}
          >
            <CompactSelectControl
              label="Emissions"
              helpText="Changes the projected emissions pathway used across these climate results."
              value={emissionTier}
              options={emissionControls}
              onChange={setEmissionTier}
              ariaLabel="GHG emission scenario"
            />
            <CompactSelectControl
              label="Horizon"
              helpText="Changes the projection timeframe shown where available. Year labels are representative horizon markers, not exact single-year values."
              value={period}
              options={horizonControls}
              onChange={setPeriod}
              ariaLabel="Time horizon"
            />
          </Box>
        }
        sx={{
          alignItems: 'flex-start',
          gap: 1,
          flexWrap: 'wrap',
          '& .MuiCardHeader-action': {
            alignSelf: 'flex-start',
            flex: { xs: '1 1 100%', sm: '0 0 auto' },
            mt: 0,
            mr: 0,
            ml: { xs: 0, sm: 2 },
          },
        }}
      />
      <CardContent>
        <Stack spacing={1.25}>
          {hazardRows.map(hazard => {
            const state = hazard.query.data?.state?.value as MetricState | undefined
            const isLoading = hazard.query.isLoading || hazard.query.isFetching
            const statusText =
              state && state !== MetricState.SUCCEEDED
                ? metricStateLabels[state] ?? 'Data unavailable'
                : hazard.query.error
                ? 'Unable to load data'
                : 'N/A'

            return (
              <Box
                key={hazard.key}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <HazardLabelLink label={hazard.label} to={hazard.analyticsPath}>
                  <ClimateHazardIcon hazardCode={hazard.hazardCode} size={24} iconSize={16} borderRadius={1} />
                  <Typography className="hazard-label" variant="subtitle2" noWrap>
                    {hazard.label}
                  </Typography>
                </HazardLabelLink>
                <Box>
                  {isLoading && !hazard.query.data ? (
                    <Skeleton variant="text" width={80} height={24} />
                  ) : hazard.score != null ? (
                    <hazard.badge score={hazard.score} size="dense" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {statusText}
                    </Typography>
                  )}
                </Box>
              </Box>
            )
          })}
        </Stack>

        {allLoading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Skeleton variant="rectangular" width="100%" height={64} />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default PropertyClimateHazardsCard
